import Vue from 'vue';
import { mapGetters } from 'vuex';
import _ from 'lodash';
import { recycleDroplet } from '@/mixins/recycle-droplet';
import { guid } from '@/mixins/guid';
import { queuedWork } from '@/mixins/queued-work';
import { diff } from 'deep-object-diff';
import droplets from '@editor/droplets/droplets.config.json';
import { siteBuilder } from '@/mixins/site-builder';

export default Vue.component('search-content', {
  template: require('./template.html'),
  mixins: [
    guid,
    recycleDroplet,
    siteBuilder,
    queuedWork,
  ],
  data() {
    // We pick the columns we want to be able to filter and sort on and ultimately
    // display
    const cols = ['name', 'url', 'type', 'status', 'createdDate'];
    // Hold a map of keys and whether or not we're currently sorting on them
    // This allows us to switch back and forth
    const sortMap = _.zipObject(cols, _.fill(Array(cols.length), -1));
    return {
      cols,
      sortKey: '',
      filterKey: '',
      sortMap,
      paginate: ['content'],
      types: [
        'page',
        'template',
        'draft',
        'dynamic page',
        'droplet',
        'puddle',
        'sectionBlock',
      ],
      activeItem: null,
      activePage: null,
      unsavedChanges: false,
      loading: false,
    };
  },
  props: ['modal'],
  computed: {
    ...mapGetters([
      'currentPage',
      'initialCurrentPage',
      'currentSite',
    ]),
    showDynamicOption() {
      const { type, addedTypes = [] } = this.currentSite;
      const isGeneral = type === 'general';
      return !isGeneral || (isGeneral && addedTypes.length > 0);
    },
    content() {
      const dynamicPages = this.getDynamicPages();
      const list = this.currentSite.pages.concat(
        this.currentSite.templates,
        this.currentSite.drafts,
        this.currentSite.recycled || [],
        dynamicPages,
      );
      const filtered = [];
      // check type filters
      _.forEach(list, (item) => {
        const type = item.type.split('.')[0] || item.type;
        if (this.types.includes(type)) {
          filtered.push(item);
        }
      });
      const sortOn = this.sortKey || this.cols[4];
      return _.chain(filtered)
        .thru((t) => {
          if (this.filterKey) {
            return _.reduce(
              t,
              (acc, row) => {
                const e = [];
                // only grab the properties that the table uses
                _.filter(row, () => {
                  _.forEach(this.cols, (c) => {
                    // clean up the values so that the
                    // searchable items are like how
                    // the template is written
                    const col = this.cleanItem(row[c], c, row);
                    e.push(col);
                  });
                });
                if (
                  _.filter(e, v =>
                    _(v)
                      .toLower()
                      .includes(this.filterKey.toLowerCase()),
                  ).length
                ) {
                  acc.push(row);
                }
                return acc;
              },
              [],
            );
          }
          return t;
        })
        .sort((a, b) => {
          // We'll sort accending by default and we'll also ignore case
          // since that may confuse non-technical users
          const ascDesc = this.sortMap[sortOn];
          a = _.isString(a[sortOn]) ? _.toLower(a[sortOn]) : a[sortOn]; // eslint-disable-line
          b = _.isString(b[sortOn]) ? _.toLower(b[sortOn]) : b[sortOn]; // eslint-disable-line

          return (a === b ? 0 : a < b ? -1 : 1) * ascDesc; // eslint-disable-line
        })
        .value();
    },
  },
  watch: {
    modal(o) {
      if (o) {
        this.unsavedChanges = false;
        this.activePage = null;
      } else {
        this.filterKey = '';
        this.activeItem = null;
      }
      this.$store.dispatch('setGlobalOpenModal', o);
    },
  },
  methods: {
    getTipText(item) {
      let msg = '';
      if (!this._canDeletePage(item)) {
        if (item.type === 'template') {
          msg = 'Template is in use.';
        } else {
          msg = 'This page has sub pages in the site map.';
        }
      }
      return msg;
    },
    searchContent(e) {
      // we user a debounce on the search modal input because the typing
      // becomes noticably slower as more content is introduced and things
      // need to query
      this._debounce(() => {
        this.filterKey = e.target.value;
      }, 250);
    },
    isChecked(type) {
      return this.types.includes(type);
    },
    syncFilter(e, type) {
      const checked = e.currentTarget.value;
      const isDroplet = type === 'droplet';
      if (checked && !this.types.includes(type)) {
        this.types.push(type);
        if (isDroplet && !this.types.includes('sectionBlock')) {
          this.types.push('sectionBlock');
        }
        if (isDroplet && !this.types.includes('puddle')) {
          this.types.push('puddle');
        }
      } else {
        const i = this.types.indexOf(type);
        this.types.splice(i, 1);
        if (isDroplet && this.types.includes('sectionBlock')) {
          const i = this.types.indexOf('sectionBlock');
          this.types.splice(i, 1);
        }
        if (isDroplet && this.types.includes('puddle')) {
          const i = this.types.indexOf('puddle');
          this.types.splice(i, 1);
        }
      }
    },
    cancel() {
      this.$log.debug('Cancel search modal');
      this.$emit('toggle-modal');
    },
    async editPage(pg) {
      this.activePage = pg;
      // if user is going to a different page than the one they're on
      // check for unsaved changes;
      if (this.currentPage._id !== pg._id) {
        // page is good to go on
        const cP = this._deepOmit(this.currentPage, this._pageDiffIgnore);
        const iP = this._deepOmit(this.initialCurrentPage, this._pageDiffIgnore);
        if (_.isEqual(cP, iP)) {
          await this.setCurrentPage();
        } else {
          // page has unsaved changes
          this.unsavedChanges = true;
          this.$log.debug('Unsaved Page Difference: ', diff(cP, iP));
        }
      } else {
        this.$emit('toggle-modal');
      }
    },
    async setCurrentPage() {
      this.$log.debug('setCurrentPage from search-content');
      const page = this.activePage;
      this.$emit('toggle-modal');
      if (this._pageTypes.includes(page.type)) {
        const { item } = await this.checkQueuedWork({ page });
        this.$store.dispatch('setOriginalPage', item);
        this.$store.dispatch('setCurrentPage', item);
        return;
      }
      this.$store.dispatch('setCanvasPage', {
        drp: page,
      });
      this.$store.dispatch('setOriginalPage', page);
    },
    async checkPage(choice) {
      if (choice === 'yes') {
        const isDroplet = !this._pageTypes.includes(this.currentPage.type);
        if (!isDroplet) {
          // revert the droplet count to the initial value
          await this.$store.dispatch('revertDropletCounts', {
            site: this.currentSite,
          });
          // revert all page data to the initial value
          await this.$store.dispatch('revertPage', this.initialCurrentPage);
        } else {
          // this will revert a droplet to when it was loaded
          const index = _.findIndex(this.currentSite.recycled, r => r._id === this.currentPage._id);
          const $vm = this;
          this.$store.dispatch('runStateFn', {
            fn() {
              $vm.currentSite.recycled.splice(index, 1, $vm.initialCurrentPage);
            },
          });
        }
        this.$store.dispatch('updateInitialPage');
        this.setCurrentPage();
      }
      if (choice === 'no') {
        this.$emit('toggle-modal');
      }
    },
    setSortKey(k) {
      this.sortKey = k;
      // Invert the value for ascDesc
      this.sortMap[k] = this.sortMap[k] * -1;
      this.activeItem = null;
    },
    cleanItem(item, col, it) {
      let i = item || '--';
      if (col === 'type') {
        i = this.setString(i);
      }
      if (col === 'createdDate' && i !== '--') {
        i = this._zoneFormat({
          date: i,
          format: 'MM-DD-YYYY hh:mm A z',
          zone: this.currentSite.timezone,
        });
      }
      if (col === 'url' && (it.type === 'template' || it.type === 'dynamic page')) {
        i = '--';
      }
      if (col === 'status') {
        i = this._startCase(it.status || 'Published');
      }
      if (it.type === 'dynamic page' && it.dynamicPageType && col === 'name') {
        i = `${i} (${this._startCase(it.dynamicPageType)})`;
      }
      return i;
    },
    highlight(ctx, query, col, it) {
      const c = this.cleanItem(ctx, col, it);
      return c.replace(
        new RegExp(query, 'ig'),
        match => `<span class='editorUi-searchHighlight'>${match}</span>`,
      );
    },
    setActive(i, item) {
      if (item && this._canDeletePage(item)) {
        this.activeItem = i;
      } else if (!item) {
        this.activeItem = null;
      }
    },
    removeCountedDroplets(list, item) {
      if (item[list] && this.currentSite[list]) {
        _.forEach(item[list], (_id) => {
          const index = _.findIndex(this.currentSite[list], f => f === _id);
          if (index >= 0) {
            this.$store.dispatch('spliceDropletVal', {
              array: this.currentSite[list],
              index,
            });
          }
        });
      }
    },
    getDynamicPages() {
      if (!this.currentSite.dynamic) {
        return [];
      }
      const pages = [];
      _.forOwn(this.currentSite.dynamic, (data, type) => {
        (data.pages || []).forEach((page) => {
          if (!page.dynamicPageType) {
            this.$store.dispatch('setGlobalValue', {
              item: page,
              key: 'dynamicPageType',
              value: type,
            });
          }
          pages.push(page);
        });
      });
      return pages;
    },
    async deleteItem(item) {
      this.setActive(null);
      if (this._canDeletePage(item)) {
        if (this._pageTypes.includes(item.type)) {
          this.loading = true;
          const n = {
            id: this.guid(),
            type: 'success',
            message: `${item.type}: ${item.name} deleted successfully!`,
          };
          try {
            let folder = false;
            if (item.type === 'page') {
              // remove from sitemap state
              await this.$store.dispatch('removeFromSiteMap', item);
              // go delete the page folder in s3
              const path = this.buildPath(item.url);
              // build path for bucket
              folder = `${this._customerFolder}/${this._sitePath}${path}`;
              // if this page is tied to a dynamic item, we need to reset it to null
              if (this.currentSite.dynamic && item.dynamicItemId) {
                // Note, to search through list of ALL dynamic types, we can map all of
                // the keys in currentSite.dynamic, in case we allow multiple site types
                // in the future
                _.forOwn(this.currentSite.dynamic, (value, type) => {
                  const itm = _.find(
                    type.items, i => i._id === item.dynamicItemId,
                  );
                  if (itm) {
                    this.$store.dispatch('setDropletVal', {
                      drp: itm,
                      key: 'pageId',
                      val: null,
                    });
                  }
                  const published = _.find(
                    type.published, p => p._id === item.dynamicItemId,
                  );
                  if (published) {
                    this.$store.dispatch('setDropletVal', {
                      drp: published,
                      key: 'pageId',
                      val: null,
                    });
                  }
                });
              }
              // if this page is tied to a group item, we need to reset it to null
              if (this.currentSite.dynamic && item.groupItemId) {
                // Note, to search through list of ALL dynamic types, we can map all of
                // the keys in currentSite.dynamic, in case we allow multiple site types
                // in the future
                _.forOwn(this.currentSite.dynamic, (value, type) => {
                  const group = _.find(
                    type.groups, group => group._id === item.groupItemId,
                  );
                  if (group) {
                    this.$store.dispatch('setDropletVal', {
                      drp: group,
                      key: 'pageId',
                      val: null,
                    });
                  }
                });
              }
            }
            if (item.type !== 'draft') {
              // remove any counted droplets that are on this page
              this.removeCountedDroplets('forms', item);
            }
            let page;
            if (item.type === 'dynamic page') {
              // if dynamic page being deleted is set as template for published items,
              // we need to reset that value to null
              const pageId = item._id;
              _.forOwn(this.currentSite.dynamic, (value) => {
                if (value.page.dynamicId === item._id) {
                  this.$store.dispatch('setDropletVal', {
                    drp: value.page,
                    key: 'dynamicId',
                    val: null,
                  });
                }
                if (value.page.groupPageId === this.currentPage._id) {
                  this.$store.dispatch('setDropletVal', {
                    drp: value.page,
                    key: 'groupPageId',
                    val: null,
                  });
                }
                // remove item from published list
                const index = _.findIndex(value.pages, p => p._id === item._id);
                if (index >= 0) {
                  this.$store.dispatch('spliceDropletVal', {
                    array: value.pages,
                    index,
                  });
                }
              });
              const updateSite = await this._SEND([{
                name: 'updateSiteAttr',
                payload: {
                  siteId: this.currentSite._id,
                  pageId,
                  attr: 'dynamic',
                  value: this.currentSite.dynamic,
                },
              }]);
              page = updateSite;
            } else {
              await this.$store.dispatch('removePage', item);
              const { _id, forms, siteMap } = this.currentSite;
              const deletePageItem = await this.$lightning([{
                name: 'deletePageItem',
                payload: {
                  site: { _id, forms, siteMap },
                  item: {
                    _id: item._id,
                    type: item.type,
                  },
                  folder,
                },
              }]);
              page = deletePageItem;
            }
            this.$log.debug(`successfully removed ${item.type} from dynamo: `, page);
          } catch (e) {
            this.$log.error('error: ', e);
            n.type = 'error';
            n.message = `${e.message || e}. This has been reported.`;
            await this._reportError({
              error: e,
              fn: 'deleteItem',
            });
          } finally {
            this.$store.dispatch('addNotification', n);
            this.loading = false;
            this._trackEvent({
              type: 'delete_page',
              meta: {
                site_name: this.currentSite.alias,
                site_type: this.currentSite.type,
                site_tier: this.currentSite.tier,
                page_name: item.name,
                page_url: item.url,
                page_type: item.type,
                blocks: item.blocks.length,
              },
            });
          }
        } else {
          this.loading = true;
          const n = {
            id: this.guid(),
            type: 'success',
            message: `${item.type}: ${item.name} deleted successfully!`,
          };
          try {
            const index = _.findIndex(this.currentSite.recycled, r => r._id === item._id);
            this.$store.dispatch('spliceDropletVal', {
              array: this.currentSite.recycled,
              index,
            });
            // save updated recycled droplet
            await this._SEND([{
              name: 'updateSiteAttr',
              payload: {
                siteId: this.currentSite._id,
                attr: 'recycled',
                value: this.currentSite.recycled,
              },
            }]);
            this.$log.debug('successfully updated recycled');
            // delete id folder stuff
            await this.checkRemoveAssets(item);
          } catch (e) {
            this.$log.error('error: ', e);
            n.type = 'error';
            n.message = `${e.message || e}. This has been reported.`;
            await this._reportError({
              error: e,
              fn: 'deleteItem',
            });
          } finally {
            this.loading = false;
            this.$store.dispatch('addNotification', n);
            this._trackEvent({
              type: 'delete_recycled_droplet',
              meta: {
                site_name: this.currentSite.alias,
                site_type: this.currentSite.type,
                site_tier: this.currentSite.tier,
                droplet_name: item.name,
              },
            });
          }
        }
        // if user is deleting the page they're on, set currentPage to homepage
        if (item._id === this.currentPage._id) {
          const homepage = _.find(this._currentSite.pages, {
            isHomepage: true,
          });
          const { item } = await this.checkQueuedWork({ page: homepage });
          this.$store.dispatch('setOriginalPage', item);
          this.$store.dispatch('setCurrentPage', item);
        }
      }
    },
    async checkRemoveAssets(item) {
      const type = this._dropletType(item);
      const droplet = droplets[type.name];
      if (droplet.storage && droplet.storage.idFolder) {
        const folder = `_droplet-assets/${type.name}/${item._id}`;
        await this._s3emptyFolder({
          path: `${this._customerFolder}/${this._sitePath}/${folder}/`,
        });
        if (droplet.storage.thumbnails) {
          const thumbFolder = `_droplet-assets/_thumbnails/${type.name}/${item._id}`;
          await this._s3emptyFolder({
            path: `${this._customerFolder}/${this._sitePath}/${thumbFolder}/`,
          });
        }
      }
    },
    buildPath(url) {
      // Since the sitemap makes links take a depth, we need to make sure
      // it is properly doing so.
      let path = url;
      if (!path.endsWith('/') && path !== '/') {
        // remove last "/" if there
        path = `${path}/`;
      }
      if (!path.startsWith('/') && path !== '/') {
        // remove the first "/" if there
        path = `/${path}`;
      }
      if (path.trim() === '') {
        path = '/';
      }
      return path;
    },
    setString(type) {
      // so we can have readable data in our search table
      let t = type;
      if (t.match(/sectionBlock/gi)) {
        t = 'section';
      } else if (t.match(/droplet/gi)) {
        let d = t.split('.')[1];
        d = this.seperateCamalCase(d);
        t = `droplet - ${d}`;
      } else if (t.match(/puddle/gi)) {
        let d = t.split('.')[1];
        d = this.seperateCamalCase(d);
        t = `puddle - ${d}`;
      }
      return t;
    },
    seperateCamalCase(str, mobile) {
      const tail = mobile ? ': ' : '';
      return str.replace(/([A-Z])/g, g => ` ${g[0].toLowerCase()}`) + tail;
    },
    genType(type) {
      return type.split('.')[0];
    },
  },
});



// WEBPACK FOOTER //
// ./src/global-view/editor/page/top-control-bar/search-content/index.js